"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";

import type { CurrentUserModel } from "@/api";

import { getOrgPersonByUserId } from "@/actions/people";
import { getUserFullNameByEmail } from "@/actions/users";
import { queryKeys } from "@/constants/query";
import { useLoggedUser } from "@/hooks/use-logged-user";
import { isValidOrgId } from "@/lib/utils";

function isUnusableNamePart(value: string | null | undefined): boolean {
  if (!value) return true;
  const trimmed = value.trim();
  if (!trimmed) return true;
  const lower = trimmed.toLowerCase();
  return lower === "unknown" || lower === "null" || lower === "undefined";
}

function formatPersonDisplayName(person: {
  firstName?: string | null;
  lastName?: string | null;
} | null | undefined): string {
  if (!person) {
    return "";
  }
  const parts = [person.firstName, person.lastName].filter(
    (p): p is string => typeof p === "string" && !isUnusableNamePart(p),
  );
  return parts.join(" ").trim();
}

function getMenuInitials(displayName: string): string {
  const base = displayName.trim();
  if (!base) {
    return "";
  }
  const parts = base.split(/\s+/);
  const first = (parts[0]?.[0] ?? "").toUpperCase();
  const second =
    parts.length > 1 && parts[1]?.[0] ? parts[1][0].toUpperCase() : "";
  return first + second;
}

/**
 * Canonical label + initials for user menu / sidebar footer.
 * Prefers org Person (My Profile) over `/me` and JWT `session.user.name` so names stay in sync.
 */
export function useProfileMenuDisplayName(): {
  displayName: string;
  initials: string;
  email: string;
  orgId: string | null;
  user: CurrentUserModel | null | undefined;
  isLoading: boolean;
} {
  const { user, orgId, isLoading } = useLoggedUser();
  const { data: session } = useSession();

  /**
   * Prefer `/me` email (same source as My Account). Auth.js JWT email can lag
   * after account switch and showed the wrong address on the sidebar hover.
   * Fall back to session only when `/me` is missing or a CIAM placeholder
   * (`oid@….onmicrosoft.com`).
   */
  const meEmail = user?.email?.trim() ?? "";
  const sessionEmail = session?.user?.email?.trim() ?? "";
  const isCiamPlaceholder = (value: string): boolean =>
    value.toLowerCase().endsWith(".onmicrosoft.com");
  const email = (() => {
    if (meEmail && !isCiamPlaceholder(meEmail)) {
      return meEmail;
    }
    if (sessionEmail && !isCiamPlaceholder(sessionEmail)) {
      return sessionEmail;
    }
    return meEmail || sessionEmail || "";
  })();
  const userId =
    typeof user?.id === "string" && user.id.length > 0 ? user.id : "";
  const orgIdForPerson =
    typeof orgId === "string" && isValidOrgId(orgId) ? orgId : null;
  const orgPersonScope = !!orgIdForPerson && userId.length > 0;

  const {
    data: orgPerson,
    isPending: orgPersonPending,
    isError: orgPersonError,
    isFetched: orgPersonFetched,
  } = useQuery({
    queryKey: queryKeys.getOrgPersonByUserId(orgIdForPerson ?? "", userId),
    queryFn: () => getOrgPersonByUserId(orgIdForPerson ?? "", userId),
    enabled: orgPersonScope,
  });

  const orgPersonName = formatPersonDisplayName(orgPerson);

  const meNameRaw =
    user?.name && user.name !== "null"
      ? user.name.replace(" null", "").trim()
      : "";
  const meName = isUnusableNamePart(meNameRaw) ? "" : meNameRaw;

  const sessionNameRaw = session?.user?.name?.trim() ?? "";
  const sessionName = isUnusableNamePart(sessionNameRaw) ? "" : sessionNameRaw;

  const shouldFetchPrisma =
    !!email &&
    (!meName ||
      (orgPersonScope && orgPersonFetched && !orgPersonName));

  const { data: prismaFullName } = useQuery({
    queryKey: ["userFullNameByEmail", email],
    queryFn: () => getUserFullNameByEmail(email),
    enabled: shouldFetchPrisma,
  });

  const prismaName =
    typeof prismaFullName === "string" &&
    !isUnusableNamePart(prismaFullName)
      ? prismaFullName.trim()
      : "";

  const emailLocal = email.includes("@")
    ? email
        .split("@")[0]!
        .split(/[._+\-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ")
    : "";

  const displayName = useMemo((): string => {
    if (orgPersonName.length > 0) {
      return orgPersonName;
    }
    if (orgPersonScope && orgPersonPending && !orgPersonError) {
      return emailLocal || "User";
    }
    if (meName.length > 0) {
      return meName;
    }
    if (sessionName.length > 0) {
      return sessionName;
    }
    if (prismaName.length > 0) {
      return prismaName;
    }
    return emailLocal || "User";
  }, [
    emailLocal,
    meName,
    orgPersonError,
    orgPersonName,
    orgPersonPending,
    orgPersonScope,
    prismaName,
    sessionName,
  ]);

  const initials = useMemo(
    () => getMenuInitials(displayName),
    [displayName],
  );

  return {
    displayName,
    initials,
    email,
    orgId: orgId ?? null,
    user,
    isLoading,
  };
}
